import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  Inject,
  isDevMode,
  ChangeDetectionStrategy
} from '@angular/core';
import { AccountsDataSource } from '../accounts.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from 'src/app/app.service';
import { tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ApiRequestService } from 'src/app/shared/api-request.service';

@Component({
  selector: 'app-accounts-selector',
  templateUrl: './accounts-selector.component.html',
  styleUrls: ['./accounts-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountsSelectorComponent implements OnInit, OnDestroy {
  // Columns to show in the view
  displayedColumns: string[] = ['id', 'name', 'subscription', 'date_created'];

  // The data source
  dataSource: AccountsDataSource;

  // The paginator and sorter objects
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    public api: ApiRequestService,
    public dialogRef: MatDialogRef<AccountsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  /**
   * Tracks full account objects for multi-page selection when returnObjects
   * is true. Updated whenever a row is toggled or a new page loads.
   */
  private selectedObjectsMap: Map<number, any> = new Map();

  /**
   * Subscription to selection.changed for cleanup on destroy.
   */
  private selectionChangedSub: Subscription | null = null;

  ngOnInit() {
    // Set multiple if it is not present and default it to false
    if (
      typeof this.data['multiple'] == 'undefined' ||
      typeof this.data['multiple'] != 'boolean'
    ) {
      this.data['multiple'] = false;
    }

    // Should object be returned instead?
    if (
      typeof this.data['returnObjects'] == 'undefined' ||
      typeof this.data['returnObjects'] != 'boolean'
    ) {
      this.data['returnObjects'] = false;
    }

    if (this.data['multiple']) {
      // Show the single record selector
      this.displayedColumns.unshift('select');
    } else {
      // Show the multiple records selector
      this.displayedColumns.push('actions');
    }

    // Construct the datasource
    this.dataSource = new AccountsDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // Set a default selection if no selected values are passed through
    if (typeof this.data['selected'] == 'undefined') {
      this.data['selected'] = [];
    }

    // Loop and select the values in the data source
    for (let i = 0; i < this.data['selected'].length; i++) {
      const item = this.data['selected'][i];
      if (this.data['returnObjects']) {
        this.dataSource.selection.select(item.id);
        this.selectedObjectsMap.set(item.id, item);
      } else {
        this.dataSource.selection.select(item);
      }
    }

    // When returnObjects is true, track selection changes to maintain
    // the full object map across paginated pages.
    if (this.data['returnObjects']) {
      this.selectionChangedSub = this.dataSource.selection.changed.subscribe(
        () => {
          this.syncSelectedObjectsMap();
        }
      );
    }

    // Get the data
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onSelectSingle(value: any) {
    this.dialogRef.close(value);
  }

  onSelectMultiple() {
    if (this.data['returnObjects']) {
      // Sync one final time to capture any current-page objects.
      this.syncSelectedObjectsMap();
      const selected: any[] = [];
      const missing: number[] = [];
      for (const id of this.dataSource.selection.selected) {
        const obj = this.selectedObjectsMap.get(id);
        if (obj) {
          selected.push(obj);
        } else {
          missing.push(id);
        }
      }
      if (missing.length > 0 && isDevMode()) {
        console.warn(
          'AccountsSelectorComponent: ' +
            missing.length +
            ' selected ID(s) could not be resolved to objects: ',
          missing
        );
      }
      this.dialogRef.close(selected);
    } else {
      this.dialogRef.close(this.dataSource.selection.selected);
    }
  }

  /**
   * Keeps the selectedObjectsMap in sync with the current page's data.
   * Adds objects for newly-selected IDs visible on the current page and
   * removes entries for deselected IDs.
   */
  private syncSelectedObjectsMap(): void {
    const currentPageRecords: any[] =
      this.dataSource.dataSubject.getValue() || [];
    const selectedIds: Set<number> = new Set(
      this.dataSource.selection.selected
    );

    // Add any newly-selected objects from the current page.
    for (const record of currentPageRecords) {
      if (selectedIds.has(record.id)) {
        this.selectedObjectsMap.set(record.id, record);
      }
    }

    // Remove deselected entries.
    for (const id of this.selectedObjectsMap.keys()) {
      if (!selectedIds.has(id)) {
        this.selectedObjectsMap.delete(id);
      }
    }
  }

  onSelectAllRecords() {
    this.dialogRef.close({
      allRecords: true,
      ids: this.dataSource.ids
    });
  }

  onDeselectAllRecords() {
    this.dataSource.selection.clear();
  }

  onClose() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    if (this.selectionChangedSub) {
      this.selectionChangedSub.unsubscribe();
    }
  }
}
